import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { articles } from '../data/articles';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';

const Ratgeber = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="ratgeber-page">
      <Navbar />
      {/* Hero glow that extends behind navbar */}
      <div className="hero-glow" />
      <main className="pt-20 relative">
        {/* Header */}
        <section className="py-24 md:py-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                AZAV-Wissen
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold tracking-tight leading-none mb-6" data-testid="ratgeber-headline">
                Ratgeber für{' '}
                <span className="text-primary">Bildungsträger</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Fachwissen rund um AZAV-Zulassung, Qualitätsmanagement und den Aufbau eines Bildungsträgers. Verständlich erklärt.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16 md:py-24 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
              {articles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group bg-card/50 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-300"
                  data-testid={`article-card-${index}`}
                >
                  {/* Category Badge */}
                  <div className="p-6 pb-0">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs uppercase tracking-wider text-primary font-semibold px-3 py-1 bg-primary/10 rounded-full">
                        {article.category}
                      </span>
                      <div className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Clock size={14} />
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 pt-2">
                    <h2 className="text-xl font-heading font-semibold text-white mb-3 group-hover:text-primary transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {article.description}
                    </p>
                    <Link to={`/ratgeber/${article.slug}`}>
                      <Button 
                        variant="outline"
                        className="w-full bg-transparent border border-white/20 hover:bg-white/5 hover:border-primary/50 text-white hover:text-primary rounded-lg py-5 transition-all group/btn"
                      >
                        Mehr lesen
                        <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 md:py-32 bg-[#050505] relative">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6">
                Fragen zu Ihrem konkreten Fall?
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
                Unsere Artikel geben einen allgemeinen Überblick. Für eine Einschätzung Ihrer individuellen Situation sprechen Sie am besten direkt mit uns.
              </p>
              <Link to="/kontakt">
                <Button 
                  className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                >
                  Kostenlose Ersteinschätzung
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default Ratgeber;
